import * as THREE from 'three';
import TWEEN from '@tweenjs/tween.js';
import {USE_LEGACY_TWEEN_GROUP} from 'components/customer/Preview3DCommon/lib/tween';
import {AnimationHandler} from 'components/customer/Preview3D/animation/entity/AnimationHandler';
import {PreviewLocation} from 'components/customer/Preview3D/types';

/**
 * Creates an animation handler for a high-lift door animation.
 *
 * The caller is responsible for setting the mesh's initial position and scale,
 * and for computing the endLiftDistance appropriate for the scene's coordinate system,
 * before invoking this handler.
 *
 * @param {THREE.Group<THREE.Object3DEventMap>} mesh - The 3D mesh group representing the door.
 * @param {number} endLiftDistance - The Z coordinate the mesh lifts to when fully open.
 *   Compute this at the call site based on the scene's coordinate system.
 * @param {PreviewLocation} [previewLocation='SPECS'] - Current preview tab location.
 * @param {boolean} isDoorOpen - Whether the door is initially open.
 * @return {AnimationHandler} An object containing `open` and `close` animation step arrays.
 */
export const highLiftAnimationHandler = (
    mesh: THREE.Group<THREE.Object3DEventMap>,
    endLiftDistance: number,
    previewLocation: PreviewLocation = 'SPECS'
): AnimationHandler => {
    const bbox = new THREE.Box3().setFromObject(mesh);
    const size = new THREE.Vector3();
    bbox.getSize(size);

    const pivot = mesh.position.clone();

    const pushOutDistance = -100;

    if (previewLocation === 'DOORS') {
        mesh.position.set(0, size.y, 0);
    }

    /**
     * Animates the mesh to lift up along the Z axis.
     *
     * @param {number} [speed=200] - Duration of the tween in milliseconds.
     * @return {Promise<void>} Resolves when the lift-up animation completes.
     */
    function liftUp(speed = 200): Promise<void> {
        return new Promise((resolve) => {
            new TWEEN.Tween(mesh.position, USE_LEGACY_TWEEN_GROUP)
                .to({z: endLiftDistance}, speed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => resolve())
                .start();
        });
    }

    /**
     * Animates the mesh to push outwards along the Y axis.
     *
     * @param {number} [speed=200] - Duration of the tween in milliseconds.
     * @return {Promise<void>} Resolves when the push-out animation completes.
     */
    function pushOut(speed = 200): Promise<void> {
        return new Promise((resolve) => {
            new TWEEN.Tween(mesh.position, USE_LEGACY_TWEEN_GROUP)
                .to({y: pushOutDistance}, speed)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onComplete(() => resolve())
                .start();
        });
    }

    /**
     * Animates the mesh to lower back down along the Z axis to the pivot position.
     *
     * @param {number} [speed=400] - Duration of the tween in milliseconds.
     * @return {Promise<void>} Resolves when the lift-down animation completes.
     */
    function liftDown(speed = 400): Promise<void> {
        return new Promise((resolve) => {
            new TWEEN.Tween(mesh.position, USE_LEGACY_TWEEN_GROUP)
                .to({z: pivot.z}, speed)
                .easing(TWEEN.Easing.Quadratic.InOut)
                .onComplete(() => resolve())
                .start();
        });
    }

    /**
     * Animates the mesh to pull back inwards along the Y axis to the pivot position.
     *
     * @param {number} [speed=400] - Duration of the tween in milliseconds.
     * @return {Promise<void>} Resolves when the push-in animation completes.
     */
    function pushIn(speed = 400): Promise<void> {
        return new Promise((resolve) => {
            new TWEEN.Tween(mesh.position, USE_LEGACY_TWEEN_GROUP)
                .to({y: pivot.y}, speed)
                .easing(TWEEN.Easing.Quadratic.InOut)
                .onComplete(() => resolve())
                .start();
        });
    }

    /**
     * Sequence to open the door: first push outwards, then lift up.
     *
     * @param {number} [speed=400] - Base duration in milliseconds.
     * @return {Promise<void>} Resolves when the open sequence completes.
     */
    const openDoor = (speed = 400) =>
        pushOut(speed).then(() => liftUp(speed * 0.5));

    /**
     * Sequence to close the door: first lower down, then pull inwards.
     *
     * @param {number} [speed=400] - Base duration in milliseconds.
     * @return {Promise<void>} Resolves when the close sequence completes.
     */
    const closeDoor = (speed = 400) =>
        liftDown(speed).then(() => pushIn(speed * 0.9));

    return {
        open: [openDoor],
        close: [closeDoor],
    };
};
